import { useState, useEffect, useContext } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";

// Custom components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";
import MDInput from "components/MDInput";

import CrudService from "services/cruds-service";
import { AuthContext } from "context";

// Must match the upload endpoint (backend/src/routes/uploads): raster images up to 5 MB.
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const MAX_SIZE = 5 * 1024 * 1024;

function Header({ user, isDemo }) {
  const authContext = useContext(AuthContext);

  const [image, setImage] = useState("");
  const [fileState, setFileState] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [notification, setNotification] = useState({
    value: false,
    color: "info",
    message: "",
  });
  const [error, setError] = useState(null);
  const [role, setRole] = useState("");
  const [id, setId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    setImage(user.profile_image);
    setId(user.id);

    (async () => {
      try {
        const response = await authContext.getRole();
        setRole(response);
      } catch (err) {
        console.error("Role fetch error:", err);
      }
    })();
  }, [user]);

  useEffect(() => {
    if (notification.value === true) {
      const timer = setTimeout(() => {
        setNotification({ value: false, color: "info", message: "" });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Release the local preview URL when it is replaced or the component unmounts.
  useEffect(() => () => imageUrl && URL.revokeObjectURL(imageUrl), [imageUrl]);

  const changeHandler = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      setError("Please choose a JPG, PNG, WEBP or GIF image.");
      return;
    }
    if (file.size > MAX_SIZE) {
      setError("Image must be 5 MB or smaller.");
      return;
    }

    const formData = new FormData();
    formData.append("attachment", file);
    setFileState(formData);
    setImageUrl(URL.createObjectURL(file));
    setError(null);
  };

  const submitHandler = async (e) => {
    e.preventDefault();

    if (isDemo) {
      setNotification({
        value: true,
        color: "secondary",
        message: "You can not update the profile image in demo version",
      });
      return;
    }

    if (!fileState || !id) {
      setError("Please choose an image first.");
      return;
    }

    setSaving(true);
    try {
      // The upload endpoint stores the new URL on the user, so no separate profile update is needed.
      const res = await CrudService.imageUpload(fileState, id);
      const url = res?.url || res?.data?.url;
      if (!url) throw new Error("Image upload failed: No URL returned.");

      setImage(url);
      setImageUrl(null);
      setFileState(null);
      if (String(authContext.user?.id) === String(id)) {
        authContext.setUser((prev) => ({ ...prev, profile_image: url }));
      }
      setNotification({
        value: true,
        color: "info",
        message: "Your profile photo has been updated",
      });
      setError(null);
    } catch (err) {
      console.error("Upload error:", err);
      setError(err?.response?.data?.message || err?.message || "Image upload failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Card id="profile">
        <MDBox
          p={2}
          component="form"
          onSubmit={submitHandler}
          encType="multipart/form-data"
        >
          <Grid
            container
            spacing={3}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <Grid item>
              <MDAvatar
                src={imageUrl ?? image}
                alt={user.name || "profile-image"}
                size="xl"
                shadow="sm"
              />
            </Grid>

            <MDInput
              type="file"
              onChange={changeHandler}
              id="avatar"
              name="attachment"
              inputProps={{ accept: ALLOWED_TYPES.join(",") }}
              sx={{ display: "none", cursor: "pointer" }}
            />

            <Grid item>
              <MDBox height="100%" mt={0.5} lineHeight={1}>
                <MDTypography variant="h5" fontWeight="medium">
                  {user.name ?? "Alex Thompson"}
                </MDTypography>
                <MDTypography variant="button" color="text" fontWeight="medium">
                  {role}
                </MDTypography>
              </MDBox>
            </Grid>

            <MDBox sx={{ ml: "auto" }} display="flex" flexDirection="column">
              <MDBox display="flex" justifyContent="flex-end">
                <MDButton
                  variant="gradient"
                  color="info"
                  size="small"
                  component="label"
                  htmlFor="avatar"
                  sx={{ marginRight: "1rem" }}
                >
                  change
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="dark"
                  size="small"
                  type="submit"
                  disabled={!fileState || saving}
                >
                  {saving ? "saving..." : "save"}
                </MDButton>
              </MDBox>

              {error && (
                <MDTypography
                  variant="caption"
                  color="error"
                  fontWeight="light"
                  pt={2}
                >
                  {error}
                </MDTypography>
              )}
            </MDBox>
          </Grid>
        </MDBox>
      </Card>

      {notification.value && (
        <MDAlert color={notification.color} mt="20px">
          <MDTypography variant="body2" color="white">
            {notification.message}
          </MDTypography>
        </MDAlert>
      )}
    </>
  );
}

export default Header;
